export const privacyContent = {
  lastUpdated: "January 27, 2026",
  sections: [
    {
      id: "introduction",
      title: "1. Introduction",
      content: `
        <p>At Devolysis, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully to understand our views and practices regarding your personal data.</p>
        <p>By accessing or using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, do not use our services.</p>
      `,
    },
    {
      id: "information-collection",
      title: "2. Information We Collect",
      content: `
        <p>We collect several types of information from and about users of our services:</p>
        <h3>2.1 Personal Information</h3>
        <p>When you contact us or request our services, we may collect:</p>
        <ul>
          <li><strong>Contact Information:</strong> Name, email address, phone number, and company details</li>
          <li><strong>Project Information:</strong> Details about your project requirements, business needs, and technical specifications</li>
          <li><strong>Communication Records:</strong> Correspondence, consultation notes, and feedback</li>
        </ul>

        <h3>2.2 Automatically Collected Information</h3>
        <p>When you visit our website, we automatically collect certain information:</p>
        <ul>
          <li><strong>Technical Data:</strong> IP address, browser type, device information, and operating system</li>
          <li><strong>Usage Data:</strong> Pages visited, time spent on pages, and navigation patterns</li>
          <li><strong>Cookies and Tracking:</strong> Information collected through cookies and similar technologies</li>
        </ul>
      `,
    },
    {
      id: "how-we-use",
      title: "3. How We Use Your Information",
      content: `
        <p>We use the information we collect for various purposes, including:</p>
        <ul>
          <li><strong>Service Delivery:</strong> To provide, maintain, and improve our development and consulting services</li>
          <li><strong>Communication:</strong> To respond to inquiries, send project updates, and provide customer support</li>
          <li><strong>Project Management:</strong> To understand your requirements and deliver customized solutions</li>
          <li><strong>Business Operations:</strong> To process contracts, manage billing, and maintain client relationships</li>
          <li><strong>Website Improvement:</strong> To analyze usage patterns and enhance user experience</li>
          <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
          <li><strong>Marketing:</strong> With your consent, to send newsletters, case studies, and relevant industry insights</li>
        </ul>
      `,
    },
    {
      id: "information-sharing",
      title: "4. Information Sharing and Disclosure",
      content: `
        <p>We respect your privacy and do not sell your personal information. We may share your information only in the following circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> Third-party vendors who assist in operations (hosting, analytics, payment processing) under strict confidentiality agreements</li>
          <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales, with notice to affected users</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights and safety</li>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
        </ul>
        <p>All third-party service providers are carefully vetted and required to maintain appropriate security measures.</p>
      `,
    },
    {
      id: "data-security",
      title: "5. Data Security",
      content: `
        <p>We implement industry-standard security measures to protect your personal information:</p>
        <ul>
          <li><strong>Encryption:</strong> SSL/TLS encryption for data transmission</li>
          <li><strong>Access Controls:</strong> Limited access to personal information on a need-to-know basis</li>
          <li><strong>Secure Storage:</strong> Protected servers and databases with regular security audits</li>
          <li><strong>Employee Training:</strong> Regular security awareness training for our team</li>
          <li><strong>Incident Response:</strong> Procedures for detecting and responding to security breaches</li>
        </ul>
        <p>While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but continuously work to maintain the highest standards.</p>
      `,
    },
    {
      id: "data-retention",
      title: "6. Data Retention",
      content: `
        <p>We retain your personal information only as long as necessary for the purposes outlined in this policy:</p>
        <ul>
          <li><strong>Active Projects:</strong> Duration of the project plus legal retention requirements</li>
          <li><strong>Client Relationships:</strong> For the duration of our business relationship</li>
          <li><strong>Legal Requirements:</strong> As required by applicable laws and regulations</li>
          <li><strong>Marketing Communications:</strong> Until you opt-out or withdraw consent</li>
        </ul>
        <p>After the retention period expires, we securely delete or anonymize your information.</p>
      `,
    },
    {
      id: "your-rights",
      title: "7. Your Rights and Choices",
      content: `
        <p>Depending on your location, you may have certain rights regarding your personal information:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
          <li><strong>Objection:</strong> Object to our processing of your information for certain purposes</li>
          <li><strong>Data Portability:</strong> Request transfer of your data to another service provider</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
          <li><strong>Withdraw Consent:</strong> Withdraw previously given consent</li>
        </ul>
        <p>To exercise these rights, please contact us at <a href="mailto:contact@devolysis.com">contact@devolysis.com</a>. We will respond to your request within 30 days.</p>
      `,
    },
    {
      id: "cookies",
      title: "8. Cookies and Tracking Technologies",
      content: `
        <p>We use cookies and similar technologies to enhance your browsing experience:</p>
        <h3>Types of Cookies We Use:</h3>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for website functionality</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site</li>
          <li><strong>Preference Cookies:</strong> Remember your settings and preferences (e.g., dark mode)</li>
          <li><strong>Marketing Cookies:</strong> Track advertising effectiveness (with your consent)</li>
        </ul>
        <p>You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.</p>
      `,
    },
    {
      id: "international-transfers",
      title: "9. International Data Transfers",
      content: `
        <p>As a global service provider, we may transfer your information to countries outside your residence. We ensure appropriate safeguards are in place:</p>
        <ul>
          <li>Standard contractual clauses approved by regulatory authorities</li>
          <li>Data processing agreements with third-party service providers</li>
          <li>Compliance with applicable data protection laws</li>
        </ul>
      `,
    },
    {
      id: "children-privacy",
      title: "10. Children's Privacy",
      content: `
        <p>Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately so we can delete it.</p>
      `,
    },
    {
      id: "changes",
      title: "11. Changes to This Policy",
      content: `
        <p>We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by:</p>
        <ul>
          <li>Posting the updated policy on our website with a new "Last Updated" date</li>
          <li>Sending email notifications for material changes</li>
          <li>Providing in-app notifications where applicable</li>
        </ul>
        <p>We encourage you to review this policy regularly to stay informed about how we protect your information.</p>
      `,
    },
    {
      id: "contact",
      title: "12. Contact Us",
      content: `
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> <a href="mailto:contact@devolysis.com">contact@devolysis.com</a></li>
          <li><strong>Phone:</strong> +1 (307) 487-8301</li>
        </ul>
        <p>We are committed to resolving any privacy concerns and will respond to your inquiry within 2 business days.</p>
      `,
    },
  ],
};
